import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui";
import { CartSync } from "@/features/cart/CartSync";
import { usePushPrompt } from "@/features/notifications/pushPrompt";
import { RealtimeBridge } from "@/features/notifications/RealtimeBridge";
import { useSystemThemeSync } from "@/lib/theme";
import { env } from "@/lib/env";
import { useAuth } from "@/stores/auth";
import { ErrorBoundary } from "./ErrorBoundary";
import { createQueryClient } from "./queryClient";

const PushPromptSheet = lazy(() => import("@/features/notifications/PushPromptSheet").then((m) => ({ default: m.PushPromptSheet })));

const PushBridge = lazy(() => import("@/features/notifications/PushBridge").then((m) => ({ default: m.PushBridge })));

/** Push glue (badge, in-app toasts, taps, subscription sync) loads after sign-in, off the first paint. */
function PushGlue() {
  const authed = useAuth((s) => s.status === "authenticated");
  if (!authed) return null;
  return (
    <Suspense fallback={null}>
      <PushBridge />
    </Suspense>
  );
}

/** The notification soft-prompt is only downloaded when a high-intent moment asks for it. */
function PushPrompt() {
  const open = usePushPrompt((s) => s.open);
  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <PushPromptSheet />
    </Suspense>
  );
}

function ThemeSync() {
  useSystemThemeSync();
  return null;
}

function SessionBootstrap() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);
  return null;
}

/** App-wide providers: error boundary, head tags, server state, Google sign-in, toasts. */
export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);
  const tree = (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeSync />
        <SessionBootstrap />
        <CartSync />
        <RealtimeBridge />
        <PushGlue />
        {children}
        <PushPrompt />
        <Toaster />
      </QueryClientProvider>
    </HelmetProvider>
  );
  return (
    <ErrorBoundary>
      {env.googleClientId ? <GoogleOAuthProvider clientId={env.googleClientId}>{tree}</GoogleOAuthProvider> : tree}
    </ErrorBoundary>
  );
}
